import { createRecordingGateway, type RecordingOptions } from '@/utils/recording-gateway';

import { type SessionDetail, sessionDetailSchema } from '../domain/session-detail.model';
import type { SessionDetailGateway } from './session-detail.gateway';

interface InMemoryOptions extends RecordingOptions<SessionDetailGateway> {
  /** The session returned by getSession (defaults to a minimal PENDING stub). */
  session?: Partial<SessionDetail>;
}

const STUB_SESSION: SessionDetail = sessionDetailSchema.parse({
  id: 'session-1',
  type: 'LinkedIn Standard',
  status: 'PENDING',
  created_at: '2026-01-01T00:00:00.000Z',
});

/**
 * Deterministic in-memory gateway for tests. Records every call and lets a test
 * force a failure or hang per method (via the shared `createRecordingGateway`),
 * so the saga's load / action / error / cancellation branches run without a
 * network. Pass `session` to shape the getSession result (e.g. a running status
 * to exercise the poll, a terminal status to stop it after one fetch).
 */
export function createInMemoryGateway(options: InMemoryOptions = {}) {
  const session: SessionDetail = { ...STUB_SESSION, ...options.session };
  return createRecordingGateway<SessionDetailGateway>(
    {
      getSession: () => session,
      pause: () => undefined,
      resume: () => undefined,
      retry: () => undefined,
      cancel: () => undefined,
      reset: () => undefined,
      retryTargets: () => undefined,
    },
    options,
  );
}
